/**
 * This is a "private" service for the NWEA assignments UI.
 *
 * Service for working with NWEA Assessment based Assign Focus.  This should generalize to other third party assessments
 * provided they can be modeled in the same way.
 *
 * We view NWEA Assessments as a set of "domains" on which a student is scored.  For a given student there is an implied
 * mapping from "domains" to scores.  This is modeled in the DB by putting domains into the mgd_levels table and score
 * ranges into the mgd_concepts table.  In EdEx code these are referred to as "grade" and "domain" respectively.  This
 * is a bit confusing because a "NWEA Domain" is stored as a "grade" while a "NWEA Score Range" is stored as a "domain"!
 *
 * This service provides an API which is expressed using NWEA terms and internally maps to the existing Assign Focus
 * terms.  Hopefully this will make the UI code more readable.
 */
(function(){
  angular
    .module('edxComponents')
    .factory('edxQuantileAssignmentService', ['classroomApi', 'assignedFocusApi', '$q', 'studentApi', QuantileAssignmentServiceFactory]);

  function QuantileAssignmentServiceFactory(classroomApi, assignedFocusApi, $q, studentApi) {
    // Score ranges don't change so cache these forever
    var cachedMeasurePromise;

    /**
     * Loads active assignments for list of students.
     *
     * This just provides a nicer result object than assignedFocusApi directly.
     *
     * @param studentIds
     * @return Promise
     */
    function loadAssignments(studentIds) {
        return assignedFocusApi.getStudentActiveLessonsWithPromise(studentIds).then(function(assignments){
          return _.map(assignments, function(assignment){
            return {
              id: assignment.assignment_id,
              assignmentType: assignment.assignment_type,
              classroomId: assignment.classroom_id,
              description: assignment.desc,
              startDate: assignment.start_date,
              endDate: assignment.end_date,
              focusId: assignment.focus_id,
              focusType: assignment.focus_type,
              name: assignment.name,
              userId: assignment.user_id,
              teacherId: assignment.teacher_id,
              type: assignment.type,
              activeEngagement: assignment.active_engagement
            };
          });
        });
      }

      
    /**
     * Create 1 or more assignments.
     *
     * This expects an array of assignments to create
     *
     * [
     *   {
     *     classroomId: ...,
     *     userId: ...,
     *     scoreRangeId: ...
     *   },
     *   ...
     * ]
     *
     * It creates a single student assignment for each entry.  The API requires a classroom ID although each assignment
     * will have only 1 student.
     *
     * @param assignments Array specifying assignments to create.
     * @return Promise
     */
    function createAssignments(assignments) {
      return $q.all(assignedFocusApi.create3PAssessmentAssignments(
        _.map(assignments, function(assignment){
          return {
            focusType:assignment.focusType,
            classroomId: assignment.classroomId,
            userId: assignment.userId,
            domainId: assignment.measureId
          };
        })
      ));
    }

    /**
     * Load all of the students for a classroom.
     *
     * This just provides a nicer result object than studentApi directly.
     *
     * Returns an array of students
     *
     * [
     *   {
     *     id: ...,
     *     firstName: ...,
     *     lastName: ...,
     *     name: ...,
     *     type: 'student'
     *   },
     *   ...
     * ]
     *
     * @param classroomId Classroom ID
     * @return Promise
     */
    function loadStudentsForClassroom(classroomId) {
      return studentApi.getStudentsForClassroomWithPromise(classroomId).then(function(students){
        return _.map(students, function(student){
          return {
            id: student.id,
            firstName: student.first_name,
            lastName: student.last_name,
            name: student.name,
            type: student.type,
            grade:student.grade
          };
        });
      });
    }

    /**
     * Load a single student.
     *
     * This just provides a nicer result object than studentApi directly.
     *
     * Returns a single student
     * {
     *   id: ...,
     *   firstName: ...,
     *   lastName: ...,
     *   name: ...,
     *   type: 'student'
     * }
     *
     * @param studentId Student's user ID
     * @return Promise
     */
    function loadStudent(studentId) {
      return studentApi.getStudentWithPromise(studentId).then(function(student){
        return {
          id: student.id,
          firstName: student.first_name,
          lastName: student.last_name,
          name: student.name,
          type: student.type,
          grade:student.grade
        };
      });
    }

    /**
     * Load NWEA domains.
     *
     * The studentId is required because of the backend API which is being reused.  We don't use any of the student
     * specific data returned.
     *
     * Returns array of domains
     *
     * [
     *   {
     *     name: ...,
     *     id: ...,
     *     ordinal: ...
     *   },
     *   ...
     * ]
     *
     * @param studentId Student's user ID
     * @return Promise
     */
    function loadMeasuresForStudent(studentId) {
        if (!cachedMeasurePromise) {
            cachedMeasurePromise = _loadMeasuresForStudent(studentId).then(function (measures) {
                return _.chain(measures).map(function (item) {
                    return {
                      id: item.code,
                      name: item.name,
                      ordinal: item.ordinal
                    };
                }).filter(function (item) {
                return item.name !== 'All Domains';
                }).value();
            });
        }
          
        return cachedMeasurePromise;
    }

    function _loadMeasuresForStudent(studentId) {
        return assignedFocusApi.getActiveThirdPartyAssessment().then(function(assessment){
            return studentApi.getStudentGrades(studentId, assessment.value).then(function(gradeResponse){
                return studentApi.getStudentDomains(studentId, gradeResponse.grades[0].id).then(function(response){
                    return response.domains;
                });
            });
        });
    }
    
    return {
      createAssignments: createAssignments,
      loadAssignments: loadAssignments,
      loadStudentsForClassroom: loadStudentsForClassroom,
      loadStudent: loadStudent,
      loadMeasuresForStudent: loadMeasuresForStudent
    };
  }
}());
